import { vec3, vec4, mat3, mat4, quat } from "_src/libs/gl-matrix";

const degree = Math.PI / 180;

export function isPowerOfTwo(number) {
  return number && !(number & (number - 1));
}

export function normalizePlane(plane) {
  const a = plane[0], b = plane[1], c = plane[2], d = plane[3];

  const length = Math.sqrt(a * a + b * b + c * c);
  const k = 1 / length;

  plane[0] = a * k;
  plane[1] = b * k;
  plane[2] = c * k;
  plane[3] = d * k;
}

export function projectPointOnPlane(dest, point, plane) {

  const t = (vec3.dot(point, plane) + plane[3]) / vec3.dot(plane, plane);

  dest[0] = point[0] - t * plane[0];
  dest[1] = point[1] - t * plane[1];
  dest[2] = point[2] - t * plane[2];

}

export function getBboxSize(bbox) {

  const x = bbox[3] - bbox[0];
  const y = bbox[4] - bbox[1];
  const z = bbox[5] - bbox[2];

  return Math.sqrt(x * x + y * y + z * z);

}

export function getBboxCenter(bbox, dest) {

  const x = 0.5 * (bbox[3] + bbox[0]);
  const y = 0.5 * (bbox[4] + bbox[1]);
  const z = 0.5 * (bbox[5] + bbox[2]);

  return vec3.set(dest, x, y, z);

}

const _vec3Tmp = vec3.create();
const _vec3Tmp2 = vec3.create();

export function getPointVectorNearestPoint(pointA, pointB, pointO, dest) {

  const AB = vec3.sub(_vec3Tmp, pointB, pointA);
  const OA = vec3.sub(_vec3Tmp2, pointO, pointA);

  const t = vec3.dot(AB, OA) / vec3.dot(AB, AB);

  vec3.scaleAndAdd(dest, pointA, AB, t);

  return t;

}

export function toRadian(angle) {
  return angle * degree;
}

export function toDegree(angle) {
  return angle / degree;
}

export function lerpValues(v1, v2, t) {
  return v1 * (1 - t) + v2 * t;
}

export const decomposeMat4 = (function() {
  const mat3Tmp = mat3.create();
  const vec3Tmp = vec3.create();

  return function(matrix, position, quaternion, scale) {
    let sx = vec3.length(vec3.set(vec3Tmp, matrix[0], matrix[1], matrix[2]));
    let sy = vec3.length(vec3.set(vec3Tmp, matrix[4], matrix[5], matrix[6]));
    let sz = vec3.length(vec3.set(vec3Tmp, matrix[8], matrix[9], matrix[10]));

    // if determine is negative, we need to invert one scale
    let det = mat4.determinant(matrix);
    if (det < 0) sx = -sx;

    vec3.set(position, matrix[ 12 ], matrix [ 13 ], matrix[ 14 ]);

    mat3.fromMat4(mat3Tmp, matrix);

    let invSX = 1 / sx;
    let invSY = 1 / sy;
    let invSZ = 1 / sz;

    mat3Tmp[0] *= invSX;
    mat3Tmp[1] *= invSX;
    mat3Tmp[2] *= invSX;

    mat3Tmp[3] *= invSY;
    mat3Tmp[4] *= invSY;
    mat3Tmp[5] *= invSY;

    mat3Tmp[6] *= invSZ;
    mat3Tmp[7] *= invSZ;
    mat3Tmp[8] *= invSZ;

    quat.fromMat3(quaternion, mat3Tmp);

    vec3.set(scale, sx, sy, sz);
  }
})();

export function createMirrorMatrix(outMatrix, plane) {
  let m = outMatrix;
  let a = plane[0];
  let b = plane[1];
  let c = plane[2];
  let d = plane[3];

  m[0] = 1-2*a*a;
  m[1] = -2*b*a;
  m[2] = -2*c*a;
  m[3] = 0.0;
  m[4] = -2*a*b;
  m[5] = 1-2*b*b;
  m[6] = -2*c*b;
  m[7] = 0.0;
  m[8] = -2*a*c;
  m[9] = -2*b*c;
  m[10]= 1-2*c*c;
  m[11]= 0.0;
  m[12] = -2*a*d;
  m[13] = -2*b*d;
  m[14]= -2*c*d;
  m[15]= 1.0;
}

export function centroid( points, centroid ) {
  vec3.set(centroid, 0, 0, 0);
  for (let point of points) {
    vec3.add(centroid, centroid, point);
  }
  return vec3.scale(centroid, centroid, 1/points.length);
}

export function planeODR( points, centroid, direction ) {
  // Find the best plane normal
  const M = [];
  for (let point of points) {
    for (let i=0; i<3; i++){
      M.push(point[i] - centroid[i]);
    }
  }

  return directionThroughSVD(points.length, 3, M, direction);
}

export function angle2D(a, b){
  const dot = a[0] * b[0] + a[1] * b[1];
  const det = a[0] * b[1] - b[0] * a[1];
  return Math.atan2(det, dot);
}

export const transformVecMat4 = (function() {

  const vec4Tmp = vec4.create();

  return function(dest, vector, matrix) {

    vec4.set(vec4Tmp, vector[0], vector[1], vector[2], 0);
    vec4.transformMat4(vec4Tmp, vec4Tmp, matrix);

    return vec3.copy(dest, vec4Tmp);

  };

})();

export const orthogonalization = (function () {
  const vec3Tmp = vec3.create();

  return function(axis, right, up) {
    let direction = vec3.set(vec3Tmp, 0.0, 0.0, 1.0);
    if (Math.abs(vec3.dot(direction, axis)) === 1.0) {
      direction = vec3.set(vec3Tmp, 0.0, 1.0, 0.0);
    }

    vec3.cross(right, axis, direction);
    vec3.normalize(right, right);
    vec3.cross(up, axis, right);
  }
})();

function getShiftedMatrix(m, n, data) {
  let matrix = [];
  matrix[0] = Array(n+1).fill(0);
  for (let i=0; i<m; i++){
    matrix[i+1] = Array(n+1);
    matrix[i+1][0] = 0;
    for (let j=0; j<n; j++){
      matrix[i+1][j+1] = data[i*n +j];
    }
  }
  return matrix;
}

function directionThroughSVD(m, n, A, vcolumn) {
  const a = getShiftedMatrix(m, n, A);
  const v = [];
  for (let i=0; i<n+1; i++){
    v[i] = Array(n+1);
  }

  const S = Array(n+1);
  const res = svdcmp(a, m, n, S, v);
  if (!res) {
    return null;
  }

  let svnumber = null;
  let minVal = Infinity;
  for (let i = 1; i<4; i++) {
    if (S[i] < minVal) {
      minVal = S[i];
      svnumber = i-1;
    }
  }

  if(svnumber == null) {
    return null;
  }

  for(let i = 0; i < n; i++) {
    vcolumn[i] = v[i + 1][svnumber + 1];
  }
  return vcolumn;

}

function condSign(a, b) {
  return (b >= 0.0) ? Math.abs(a) : -Math.abs(a);
}

function svdcmp(a, m, n, w, v)
{
  let i,its,j,jj,k,l;
  let anorm,c,f,g,h,s,scale,x,y,z;

  const rv1 = Array(n+1);
  g=scale=anorm=0.0;
  l=0;
  for (let i=1; i<=n; i++){
    l=i+1;
    rv1[i]=scale*g;
    g=s=scale=0.0;
    if (i <= m) {
      for (k=i;k<=m;k++) {
        scale += Math.abs(a[k][i]);
      }
      if (scale) {
        for (k=i;k<=m;k++) {
          a[k][i] /= scale;
          s += a[k][i]*a[k][i];
        }
        f=a[i][i];
        g = -condSign(Math.sqrt(s),f);
        h=f*g-s;
        a[i][i]=f-g;
        for (j=l;j<=n;j++) {
          for (s=0.0,k=i;k<=m;k++) {
            s += a[k][i]*a[k][j];
          }
          f=s/h;
          for (k=i;k<=m;k++) {
            a[k][j] += f*a[k][i];
          }
        }
        for (k=i;k<=m;k++) {
          a[k][i] *= scale;
        }
      }
    }
    w[i]=scale *g;
    g=s=scale=0.0;
    if (i <= m && i != n) {
      for (k=l;k<=n;k++) {
        scale += Math.abs(a[i][k]);
      }
      if (scale) {
        for (k=l;k<=n;k++) {
          a[i][k] /= scale;
          s += a[i][k]*a[i][k];
        }
        f=a[i][l];
        g = -condSign(Math.sqrt(s),f);
        h=f*g-s;
        a[i][l]=f-g;
        for (k=l;k<=n;k++) {
          rv1[k]=a[i][k]/h;
        }
        for (j=l;j<=m;j++) {
          for (s=0.0,k=l;k<=n;k++) {
            s += a[j][k]*a[i][k];
          }
          for (k=l;k<=n;k++) {
            a[j][k] += s*rv1[k];
          }
        }
        for (k=l;k<=n;k++) {
          a[i][k] *= scale;
        }
      }
    }
    anorm=Math.max(anorm,(Math.abs(w[i])+Math.abs(rv1[i])));

  }

  for (i=n;i>=1;i--) {
    if (i < n) {
      if (g) {
        for (j=l;j<=n;j++) {
          v[j][i] = (a[i][j] / a[i][l]) / g;
        }
        for (j=l;j<=n;j++) {
          for (s=0.0,k=l;k<=n;k++) {
            s += a[i][k]*v[k][j];
          }
          for (k=l;k<=n;k++) {
            v[k][j] += s*v[k][i];
          }
        }
      }
      for (j=l;j<=n;j++) {
        v[i][j]=v[j][i]=0.0;
      }
    }
    v[i][i]=1.0;
    g=rv1[i];
    l=i;
  }
  for (i=Math.min(m,n);i>=1;i--) {
    l=i+1;
    g=w[i];
    for (j=l;j<=n;j++) {
      a[i][j]=0.0;
    }
    if (g) {
      g=1.0/g;
      for (j=l;j<=n;j++) {
        for (s=0.0,k=l;k<=m;k++) {
          s += a[k][i]*a[k][j];
        }
        f=(s/a[i][i])*g;
        for (k=i;k<=m;k++) {
          a[k][j] += f*a[k][i];
        }
      }
      for (j=i;j<=m;j++) {
        a[j][i] *= g;
      }
    } else {
      for (j=i;j<=m;j++) {
        a[j][i]=0.0;
      }
    }
    ++a[i][i];
  }

  let flag,nm;
  nm=0;
  for (k=n;k>=1;k--) {
    for (its=1;its<=30;its++) {
      flag=1;
      for (l=k;l>=1;l--) {
        nm=l-1;
        if (Math.abs(rv1[l])+anorm == anorm) {
          flag=0;
          break;
        }
        if (Math.abs(w[nm])+anorm == anorm) {
          break;
        }
      }
      if (flag) {
        c=0.0;
        s=1.0;
        for (i=l;i<=k;i++) {
          f=s*rv1[i];
          rv1[i]=c*rv1[i];
          if (Math.abs(f)+anorm == anorm) {
            break;
          }
          g=w[i];
          h=pythag(f,g);
          w[i]=h;
          h=1.0/h;
          c=g*h;
          s = -f*h;
          for (j=1;j<=m;j++) {
            y=a[j][nm];
            z=a[j][i];
            a[j][nm]=y*c+z*s;
            a[j][i]=z*c-y*s;
          }
        }
      }
      z=w[k];
      if (l == k) {
        if (z < 0.0) {
          w[k] = -z;
          for (j=1;j<=n;j++) {
            v[j][k] = -v[j][k];
          }
        }
        break;
      }
      if (its == 30)
      {
        return false;
      }
      x=w[l];
      nm=k-1;
      y=w[nm];
      g=rv1[nm];
      h=rv1[k];
      f=((y-z)*(y+z)+(g-h)*(g+h))/(2.0*h*y);
      g=pythag(f,1.0);
      f=((x-z)*(x+z)+h*((y/(f+condSign(g,f)))-h))/x;
      c=s=1.0;
      for (j=l;j<=nm;j++) {
        i=j+1;
        g=rv1[i];
        y=w[i];
        h=s*g;
        g=c*g;
        z=pythag(f,h);
        rv1[j]=z;
        c=f/z;
        s=h/z;
        f=x*c+g*s;
        g = g*c-x*s;
        h=y*s;
        y *= c;
        for (jj=1;jj<=n;jj++) {
          x=v[jj][j];
          z=v[jj][i];
          v[jj][j]=x*c+z*s;
          v[jj][i]=z*c-x*s;
        }
        z=pythag(f,h);
        w[j]=z;
        if (z) {
          z=1.0/z;
          c=f*z;
          s=h*z;
        }
        f=c*g+s*y;
        x=c*y-s*g;
        for (jj=1;jj<=m;jj++) {
          y=a[jj][j];
          z=a[jj][i];
          a[jj][j]=y*c+z*s;
          a[jj][i]=z*c-y*s;
        }
      }
      rv1[l]=0.0;
      rv1[k]=f;
      w[k]=x;
    }
  }
  return true;
}

function pythag(a, b) {
  const absa = Math.abs(a);
  const absb = Math.abs(b);
  if (absa > absb) {
    return absa * Math.sqrt(1.0 + (absb / absa) * (absb / absa));
  } else {
    return absb == 0.0 ? 0.0 : absb*Math.sqrt(1.0+(absa/absb)*(absa/absb));
  }
}
